import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

/**
 * Address autocomplete via backend (proxy):
 *  - /api/places/autocomplete → Google Places (New) o Nominatim
 *  - /api/places/details      → solo per risultati Google (place_id)
 *
 * Nessuna dipendenza da Google JS sul frontend. La key Places resta server-side.
 */
export default function AddressAutocomplete({
  value, onChange, onSelect, onFocus: onFocusProp, onBlur: onBlurProp,
  placeholder = 'es. Via Roma 10 Milano, oppure Roma, oppure Lazio',
  inputClassName = 'fp__input',
}) {
  const [predictions, setPredictions] = useState([]);
  const [source, setSource] = useState(null);   // 'google' | 'nominatim' | null
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const sessionRef = useRef(crypto.randomUUID?.() || String(Math.random()));
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleType = (q) => {
    onChange(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) {
      setPredictions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    setOpen(true);
    debounceRef.current = setTimeout(() => fetchSuggestions(q.trim()), 250);
  };

  const fetchSuggestions = async (q) => {
    try {
      const { data } = await api.get('/api/places/autocomplete', {
        params: { q, session: sessionRef.current },
      });
      setPredictions(data.results || []);
      setSource(data.source);
    } catch {
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePick = async (pred) => {
    setOpen(false);
    setPredictions([]);
    inputRef.current?.blur(); // chiudi la tastiera mobile appena scelgo dal menu
    onChange(pred.label);

    // Nominatim ha già lat/lng (e talvolta un boundingbox)
    if (pred.lat != null && pred.lng != null) {
      onSelect({ lat: pred.lat, lng: pred.lng, label: pred.label, viewport: pred.viewport || null });
      return;
    }

    // Google: fetch details col place_id
    if (!pred.place_id) return;
    try {
      const { data } = await api.get('/api/places/details', {
        params: { place_id: pred.place_id, session: sessionRef.current },
      });
      if (data.lat != null && data.lng != null) {
        onSelect({ lat: data.lat, lng: data.lng, label: data.label || pred.label, viewport: data.viewport || null });
        onChange(data.label || pred.label);
      }
      // Nuova sessione per il prossimo autocomplete (ottimizzazione costo Google)
      sessionRef.current = crypto.randomUUID?.() || String(Math.random());
    } catch (e) {
      console.error('details failed', e);
    }
  };

  const sourceLabel = source === 'google' ? 'Google Places'
    : source === 'nominatim' ? 'OpenStreetMap'
    : '…';

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        className={inputClassName}
        placeholder={placeholder}
        value={value || ''}
        onChange={(e) => handleType(e.target.value)}
        onFocus={() => { if (predictions.length > 0) setOpen(true); onFocusProp && onFocusProp(); }}
        onBlur={() => { onBlurProp && onBlurProp(); }}
        autoComplete="off"
      />
      {open && (loading || predictions.length > 0 || (value && value.length >= 2)) && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
            background: 'var(--bg-elev)', border: '1px solid var(--border)',
            borderRadius: 8, boxShadow: '0 6px 20px oklch(0 0 0 / 0.12)',
            maxHeight: 280, overflowY: 'auto', zIndex: 50,
          }}
        >
          {loading && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--fg-muted)' }}>Cerco…</div>
          )}
          {!loading && predictions.map((p, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handlePick(p)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 12px', background: 'transparent', border: 0,
                borderBottom: i < predictions.length - 1 ? '1px solid var(--border)' : 0,
                cursor: 'pointer', fontSize: 12.5, color: 'var(--fg)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ fontWeight: 500 }}>{p.main || p.label}</div>
              {p.secondary && (
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
                  {p.secondary}
                </div>
              )}
            </button>
          ))}
          {!loading && predictions.length === 0 && value && value.length >= 2 && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--fg-muted)' }}>Nessun risultato</div>
          )}
          <div style={{
            padding: '4px 10px', fontSize: 9.5, color: 'var(--fg-subtle)',
            textAlign: 'right', borderTop: '1px solid var(--border)',
            textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            {sourceLabel}
          </div>
        </div>
      )}
    </div>
  );
}
